import { Grid, Card, CardContent, CardHeader, Typography, Divider, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { Office } from '../../../../models/office';
import { SkeletonIndicator } from '../../../../components/skeleton-indicator.component';
import { PHONENUMBER_FORMAT, ZIPCODE_FORMAT } from '../../../../constants';
import { formatValue } from '../../../../utils/data-format';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface OfficeInfoProps {
  office?: Office;
  isLoading?: boolean;
}

interface ComponentCell {
  isLoading: any;
  heading: string;
  value: any;
}

const styling = {
  root: { borderRadius: '10px' },
  textColor: { color: '$primary-color' },
  headingStyle: { color: '#0000b5', paddingBottom: '10px' },
  labelStyle: { color: '#0000b5', fontSize: '14px' },
  valueStyle: { color: '#000', fontWeight: '500', fontSize: '13px' },
};

const ComponentCell = ({ isLoading, heading, value }: ComponentCell) => {
  return (
    <Grid container spacing={0.5}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Typography variant="body1" color="textPrimary" style={styling.labelStyle}>
          {heading}
        </Typography>
      </Grid>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        {!isLoading ? (
          <Typography variant="body1" color="text.secondary" style={styling.valueStyle}>
            {value ? value : 'N/A'}
          </Typography>
        ) : (
          <SkeletonIndicator isLoading={isLoading} count={1} variant="h6" />
        )}
      </Grid>
    </Grid>
  );
};

export const OfficeInfo = ({ office, isLoading }: OfficeInfoProps) => {
  return (
    <Card elevation={0} sx={{ mr: 2 }}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            OFFICE INFO
          </Typography>
        }
      />
      <Divider sx={{ borderBottomWidth: '2px' }} />
      <CardContent>
        <Grid container spacing={2} className="custBoxValues">
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Customer Name" value={office?.customerName} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Office Type" value={office?.officeTypeName} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Account Managers" value={office?.accountManagerNames} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Office Code" value={office?.officeCode} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Builder Code" value={office?.builderCode} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Invoice Due Days" value={office?.invoiceTermText} />
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Contact Email" value={office?.contactEmail} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Contact Phone"
                  value={formatValue(office?.contactPhone?.toString() ?? '', PHONENUMBER_FORMAT, '#').formattedValue}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Street Address" value={office?.contactStreet} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell isLoading={isLoading} heading="City" value={office?.contactCity} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell isLoading={isLoading} heading="State" value={office?.contactStateName} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Zip Code"
                  value={formatValue(office?.contactZip?.toString() ?? '', ZIPCODE_FORMAT, '#').formattedValue}
                />
              </Grid>
            </Grid>
          </Grid>
          {
            office?.syncWithDux && (
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <Grid item lg={6} sm={6} xl={6} xs={6}>
                  <ComponentCell isLoading={isLoading} heading="DUX Synced" value={office?.isDuxSyncSuccess ? 'Success' : 'Fail'} />
                </Grid>
              </Grid>
            )
          }
        </Grid>
      </CardContent>
    </Card>
  );
};
